<template>
  <el-dialog
    class="size-select"
    title="选择来源尺码表"
    :visible="visible"
    width="min(94vw, 1040px)"
    top="6vh"
    append-to-body
    @close="$emit('close')"
  >
    <div class="size-select__filters">
      <label for="size-template-source">来源店铺</label>
      <el-select
        id="size-template-source"
        v-model="shopId"
        filterable
        placeholder="请选择来源店铺"
        size="small"
        @change="changeSource"
        ><el-option v-for="shop in shops" :key="shop.shop_id" :label="shop.shop_name" :value="shop.shop_id"
      /></el-select>
      <el-button size="small" icon="el-icon-refresh" :disabled="!shopId || loading" @click="load(1)"
        >刷新列表</el-button
      >
      <span class="size-select__count">共 {{ total }} 张尺码表</span>
    </div>
    <div class="size-select__types" role="group" aria-label="模板类型">
      <span>模板类型</span>
      <button
        v-for="type in types"
        :key="type[0]"
        type="button"
        :class="{ active: subType === type[0] }"
        :aria-pressed="subType === type[0]"
        :disabled="!shopId"
        @click="changeType(type[0])"
      >
        {{ type[1] }}
      </button>
    </div>
    <div class="size-select__notice">
      <i class="el-icon-info" aria-hidden="true"></i
      >可选择其他店铺的尺码表，执行时自动复制到目标店铺。点击模板名称选择，点击表格放大查看。
    </div>
    <div v-if="error" role="alert" class="size-select__error">
      <i class="el-icon-warning-outline" aria-hidden="true"></i>{{ error
      }}<el-button type="text" size="small" @click="load(page)">重新加载</el-button>
    </div>
    <div v-loading="loading" element-loading-text="正在读取尺码表…" class="size-select__content">
      <div v-if="list.length" class="size-select__list">
        <article
          v-for="item in list"
          :key="item.template_id"
          class="size-select__card"
          :class="{
            chosen: isSelected(item),
            unavailable: !item.valid
          }"
        >
          <button
            type="button"
            class="size-select__choice"
            :disabled="!item.valid"
            :aria-pressed="isSelected(item)"
            @click="selected = item"
          >
            <span class="size-select__name" :title="item.name || '未命名尺码表'">{{
              item.name || '未命名尺码表'
            }}</span>
            <i :class="isSelected(item) ? 'el-icon-success' : 'el-icon-circle-check'" aria-hidden="true"></i>
          </button>
          <div class="size-select__preview">
            <doudian-size-chart-preview :chart="item.chart" :empty-text="item.error || '无法预览'" />
          </div>
          <div class="size-select__metadata">
            <span class="size-select__badge">{{ typeName(item.template_sub_type) }}</span>
            <span v-if="item.chart"
              >{{ (item.chart.configTable || []).length }} 个尺码 ·
              {{ (item.chart.selectedSpecs || []).length }} 项参数</span
            >
          </div>
          <div class="size-select__id" :title="String(item.template_id)">ID：{{ item.template_id }}</div>
          <p v-if="!item.valid" class="size-select__invalid">{{ item.error || '模板数据不完整，暂不可选择' }}</p>
        </article>
      </div>
      <div v-else-if="!loading && !error" class="size-select__empty">
        <i :class="shopId ? 'el-icon-document' : 'el-icon-shop'" aria-hidden="true"></i>
        <strong>{{ shopId ? '暂无匹配的尺码表' : '先选择来源店铺' }}</strong>
        <span>{{
          shopId ? '可以切换模板类型，或在店铺新增尺码表后刷新。' : '选择店铺后，可按类型浏览并选择一张尺码表。'
        }}</span>
      </div>
    </div>
    <div class="size-select__pagination">
      <el-pagination
        small
        :current-page="page"
        :page-size="20"
        :total="total"
        layout="prev, pager, next, total"
        @current-change="load"
      />
    </div>
    <div slot="footer" class="size-select__footer">
      <div class="size-select__selection">
        <i :class="selected ? 'el-icon-circle-check' : 'el-icon-info'" aria-hidden="true"></i
        ><span :title="selected && selected.name">{{
          selected ? '已选：' + (selected.name || '未命名尺码表') : '请选择一张尺码表'
        }}</span>
      </div>
      <el-button size="small" @click="$emit('close')">取消</el-button>
      <el-button
        size="small"
        class="size-select__confirm"
        type="primary"
        :disabled="!selected || loading || !!error"
        @click="confirm"
        >确定选择</el-button
      >
    </div>
  </el-dialog>
</template>

<script>
import DoudianSizeChartPreview from './DoudianSizeChartPreview.vue'
import { getDoudianSizeChartTemplates } from '@/api/main/doudianBatchEdit'
export default {
  name: 'DoudianSizeTemplateSelectorDialog',
  components: { DoudianSizeChartPreview },
  props: { visible: Boolean, shops: { type: Array, default: () => [] } },
  data: () => ({
    shopId: null,
    subType: '',
    list: [],
    selected: null,
    page: 1,
    total: 0,
    loading: false,
    error: '',
    request: 0,
    types: [
      ['', '全部'],
      ['clothing', '服装'],
      ['children_clothing', '童装'],
      ['shoes', '鞋靴'],
      ['undies', '内衣']
    ]
  }),
  watch: {
    visible(value) {
      this.request++
      this.selected = null
      this.error = ''
      this.list = []
      this.total = 0
      this.loading = false
      if (value && this.shopId) this.load(1)
    }
  },
  beforeDestroy() {
    this.request++
  },
  methods: {
    isSelected(item) {
      return (
        !!this.selected &&
        this.selected.template_id === item.template_id &&
        this.selected.source_shop_id === this.shopId
      )
    },
    typeName(value) {
      const type = this.types.find((item) => item[0] === value)
      return type && value ? type[1] : '尺码表'
    },
    changeType(value) {
      if (this.subType === value) return
      this.subType = value
      this.load(1)
    },
    changeSource() {
      this.selected = null
      this.load(1)
    },
    async load(page) {
      const request = ++this.request
      this.page = page
      this.selected = null
      this.list = []
      this.error = ''
      this.total = 0
      if (!this.shopId) return
      this.loading = true
      try {
        const response = await getDoudianSizeChartTemplates({
          source_shop_id: this.shopId,
          page,
          limit: 20,
          template_sub_type: this.subType
        })
        if (request !== this.request || !this.visible || this._isDestroyed) return
        if (Number(response.code) !== 200) throw new Error(response.message || '读取尺码表失败')
        this.list = response.data.list || []
        this.total = Number(response.data.total || 0)
      } catch (error) {
        if (request === this.request) this.error = error.message || '读取失败，请重试'
      } finally {
        if (request === this.request) this.loading = false
      }
    },
    confirm() {
      if (this.selected && !this.loading && !this.error)
        this.$emit('confirm', JSON.parse(JSON.stringify(this.selected)))
    }
  }
}
</script>

<style scoped>
.size-select ::v-deep .el-dialog {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  overflow: hidden;
  border-radius: 8px;
  color: #1a1a2e;
}
.size-select ::v-deep .el-dialog__header {
  flex-shrink: 0;
  padding: 18px 22px;
  border-bottom: 1px solid #e0e0ec;
  background: #fafafa;
}
.size-select ::v-deep .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
}
.size-select ::v-deep .el-dialog__body {
  min-height: 0;
  overflow: auto;
  padding: 18px 22px 0;
}
.size-select ::v-deep .el-dialog__footer {
  flex-shrink: 0;
  padding: 14px 22px;
  border-top: 1px solid #e0e0ec;
}
.size-select__filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 12px;
}
.size-select__filters label {
  flex-shrink: 0;
  color: #666;
}
.size-select__filters .el-select {
  width: 300px;
  max-width: 100%;
  min-width: 0;
}
.size-select__count {
  margin-left: auto;
  color: #999;
}
.size-select__types {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  margin-top: 16px;
  padding-bottom: 4px;
  font-size: 12px;
  white-space: nowrap;
}
.size-select__types > span {
  margin-right: 6px;
  color: #666;
}
.size-select__types button {
  flex-shrink: 0;
  height: 28px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: #f3f4f6;
  color: #666;
  font: inherit;
  cursor: pointer;
}
.size-select__types button.active {
  border-color: #e7bcb8;
  background: #fff2f0;
  color: #c8443c;
}
.size-select__types button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.size-select__notice {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 4px;
  background: #f5f6f8;
  color: #909399;
  font-size: 12px;
  line-height: 20px;
}
.size-select__notice i {
  margin-top: 3px;
}
.size-select__content {
  min-height: 260px;
  max-height: 48vh;
  overflow: auto;
}
.size-select__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 3px;
}
.size-select__card {
  min-width: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e0e0ec;
  border-radius: 6px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.size-select__card:not(.unavailable):hover,
.size-select__card:focus-within {
  border-color: #d7a9a5;
}
.size-select__card.chosen {
  border-color: #c8443c;
  box-shadow: 0 0 0 1px #c8443c;
}
.size-select__card.unavailable {
  background: #fafafa;
}
.size-select__choice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 12px;
  border: 0;
  border-bottom: 1px solid #f0f0f3;
  background: #fff;
  text-align: left;
  color: #303133;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.size-select__choice:disabled {
  color: #999;
  cursor: not-allowed;
}
.size-select__choice i {
  color: #c0c4cc;
  font-size: 18px;
}
.chosen .size-select__choice {
  background: #fff7f5;
}
.chosen .size-select__choice i {
  color: #c8443c;
}
.size-select__name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 600;
}
.size-select__preview {
  padding: 12px;
  text-align: center;
  background: #f8f9fb;
}
.size-select__preview ::v-deep .size-chart-preview > span {
  display: block;
}
.size-select__preview ::v-deep .size-chart-preview__card {
  width: 100%;
  height: 148px;
  padding: 12px;
}
.size-select__preview ::v-deep .size-chart-preview__mini {
  transform: scale(0.46);
}
.size-select__metadata {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px 4px;
  color: #909399;
  font-size: 11px;
}
.size-select__badge {
  padding: 2px 6px;
  border-radius: 3px;
  background: #f0f1f4;
  color: #666;
}
.size-select__id {
  overflow: hidden;
  padding: 4px 12px 12px;
  color: #999;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.size-select__invalid {
  margin: 0;
  padding: 0 12px 12px;
  color: #b97932;
  font-size: 12px;
  line-height: 18px;
  overflow-wrap: anywhere;
}
.size-select__empty {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  box-sizing: border-box;
  text-align: center;
  color: #999;
  font-size: 12px;
}
.size-select__empty i {
  color: #c8ccd4;
  font-size: 42px;
}
.size-select__empty strong {
  color: #606266;
  font-size: 14px;
}
.size-select__pagination {
  display: flex;
  justify-content: flex-end;
  overflow-x: auto;
  margin-top: 14px;
  padding: 12px 0;
  border-top: 1px solid #ececf2;
}
.size-select__footer {
  display: flex;
  align-items: center;
  gap: 10px;
}
.size-select__selection {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  text-align: left;
  color: #666;
  font-size: 12px;
}
.size-select__selection span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.size-select__selection i {
  color: #c8443c;
}
.size-select__confirm {
  border-color: #c8443c;
  background: #c8443c;
}
.size-select__confirm:not(:disabled):hover {
  border-color: #b73a33;
  background: #b73a33;
}
.size-select__error {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  color: #c8443c;
  font-size: 12px;
  overflow-wrap: anywhere;
}
@media (max-width: 800px) {
  .size-select__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 560px) {
  .size-select__content,
  .size-select__empty {
    min-height: 160px;
  }
  .size-select ::v-deep .el-dialog__header,
  .size-select ::v-deep .el-dialog__footer {
    padding: 14px;
  }
  .size-select ::v-deep .el-dialog__body {
    padding: 14px 14px 0;
  }
  .size-select__filters label {
    width: 100%;
  }
  .size-select__filters .el-select {
    width: 100%;
  }
  .size-select__list {
    grid-template-columns: minmax(0, 1fr);
  }
  .size-select__footer {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .size-select__selection {
    flex: 1 0 100%;
  }
}
</style>
